"use client";

// EtherModal — THE sheet (law_sheet_rises, 2026-10-08; it revises
// law_modal_quiet). Every modal, drawer and stepped window in the product is
// one of these: it RISES from the bottom edge of the window, on a desktop as on
// a phone, and unfolds as it lands (the head, then the words, then the foot).
// It never grows or shrinks with what is inside it: a sheet with sections,
// steps or words that load is TALL for its whole life; a short one (a confirm,
// a picker) is measured once as it opens and then held. One padding all round.
// The room behind leans under one veil; netti rests there.
//
//   <EtherModal open={!!id} onClose={close} labelledBy={titleId} hue={project.color}
//     header={<><h2 id={titleId} className="eth-title">…</h2><p className="eth-meta">…</p></>}
//     footer={<PillButton>…</PillButton>}
//     nav={{ sections, section, onSection, prev, next, stepKey, back }}
//     share={{ kind: "ref", … }}>
//     …the scrolling body…
//   </EtherModal>
//
// THE HEAD, for every caller: the grip (pull it down to let the sheet go), your
// `header` beside the close, then — when `nav.sections` has two or more — the
// NAVIGATOR: the sections as the dashboard's own words, the open one leading
// (MiniNavSections). `nav.back` is the one way back, only from something opened
// inside the sheet.
//
// THE FOOT, one row, the sheet's own padding: quiet things on the left
// (`footStart`; on a phone the share bar lives here), then the steps between
// siblings bottom right (‹ › with the neighbour's name on hover), then your
// `footer`, the one action, in the corner.
//
// What it owns so a caller never has to:
//   · a portal to <body> (the OS content surface is transformed and is one
//     stacking context at z 1; a fixed overlay inside it would be captured)
//   · the veil as a SIBLING frost layer, constant radius, opacity-only motion:
//     the sheet's ONE frost (the sheet itself is near-solid, so its rise costs
//     the compositor one transform and nothing else)
//   · netti rests behind it (`data-organism-frost`, read by OrganismFrostSensor)
//   · `data-noswipe` so a drag inside never reaches the shell's section swipe
//   · focus: moved in on open, trapped while open, returned to the opener after
//   · Esc closes (after `onEscape` has had the chance to step back a level);
//     the veil closes; the page behind holds still (lib/os/scrollLock: counted,
//     so two overlapping holders can never leave the page locked), and only the
//     TOP sheet's scroller answers a wheel or a touch
//   · ← / → in ONE place: between siblings when there are steps, else between
//     sections; a sideways swipe on the body does the same on touch
//   · the grip (any pointer) and, on a phone, the whole head pull the sheet
//     down; far enough, or flicked, and it goes
//   · `share`: what this sheet is about, published for the share bar
//     (lib/os/sheetShare), which lifts over the veil on a wide screen
//   · the exit plays before unmount; reduced motion fades only
//
// Two of these never stack (each traps focus and answers the same Esc). A
// sheet that opens something else inside itself shows it in its own body with
// a way back (RefReader's InlineRef).
//
// Styles: ./ether.css (class prefix eth-). Pure helpers: ./etherNav.

import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { MiniNavSections, type MiniNavProps, type MiniNavStep } from "../os/MiniNav";
import { holdGestures, lockScroll, markSheetOpen } from "../../lib/os/scrollLock";
import { OWN_SWIPE, canScrollX, isTabStop, sheetStep, sheetSteps, stepDirection, swipeAxis, swipeStep, tabWrap } from "./etherNav";
import SheetShareBar, { type SheetShare } from "./SheetShareBar";
import "./ether.css";

export type { SheetShare } from "./SheetShareBar";

export interface EtherModalProps {
  open: boolean;
  onClose: () => void;
  labelledBy?: string;
  /** a project colour (any CSS colour); null → the organism's own */
  hue?: string | null;
  /** pinned above the scrolling body, beside the close (the navigator sits under it) */
  header?: React.ReactNode;
  /** the one action, in the foot's bottom-right corner */
  footer?: React.ReactNode;
  /** quiet things at the foot's left (letting a connection go, a hint) */
  footStart?: React.ReactNode;
  /** the scrolling body */
  children: React.ReactNode;
  /** sm 520px (a confirm, a picker) · md 680px (a drawer, a story) · lg 820px · xl 1160px (a studio) */
  size?: "sm" | "md" | "lg" | "xl";
  /**
   * tall: one fixed height for the sheet's whole life (default for anything
   * with sections, steps, or a size of lg and up) · fit: measured once as it
   * opens, then held. A sheet never grows or shrinks with what it shows.
   */
  height?: "tall" | "fit";
  /** the sheet's navigator: sections (shown as the navigator's words), steps between siblings, the way back */
  nav?: MiniNavProps | null;
  /** Esc asks this first; return true when it stepped back a level and the sheet should stay */
  onEscape?: () => boolean;
  /**
   * A selector for the thing this sheet is about, in the list behind the veil
   * (`[data-look="story:<id>"]`): lit there while the sheet is open, so the
   * room shows where you were when the sheet goes.
   */
  lookAt?: string | null;
  /** what this sheet is about, for the share bar (lifted over the veil; in the foot on a phone) */
  share?: SheetShare | null;
  /** a class on the sheet's root, for a caller's own scoped styles (`.eth-root.my-sheet .eth-panel …`) */
  className?: string;
  /** inline style on the panel, for publishing a caller's custom properties (a themed ground) */
  panelStyle?: React.CSSProperties;
}

/** Light the thing a sheet is about in the list behind the veil (never scrolls the room under a sheet). */
function useLookAt(open: boolean, lookAt: string | null | undefined) {
  useEffect(() => {
    if (!open || !lookAt || typeof document === "undefined") return;
    let el: Element | null = null;
    try {
      el = document.querySelector(lookAt);
    } catch {
      return;
    }
    if (!el) return;
    el.setAttribute("data-looked", "");
    return () => el?.removeAttribute("data-looked");
  }, [open, lookAt]);
}

/** the way down: matches `ethSink` in ether.css */
const EXIT_MS = 260;
const DISMISS_PX = 120; // a pull further than this lets the sheet go
const FLICK_V = 0.55; // px/ms: a fast flick lets it go whatever the distance
/** how long a stepped-in body is left alone before its children animate again (the swipe's 0.5s) */
const STEP_SETTLE_MS = 520;
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const noSubscribe = () => () => {};
const isClient = () => true;
const isServer = () => false;

// useLayoutEffect warns on the server; the modal only ever paints on the client.
const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/** a stored colour is user data: only a real CSS colour may reach the ground. */
function safeHue(hue: string | null | undefined): string | null {
  if (!hue) return null;
  try {
    return typeof CSS !== "undefined" && CSS.supports("color", hue) ? hue : null;
  } catch {
    return null;
  }
}

/** the sheet's real Tab stops, in order (a button taken out of the Tab order, like the ticker's words, is not one) */
function focusablesIn(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => isTabStop(el) && !el.hasAttribute("inert") && !el.closest("[inert]") && el.getClientRects().length > 0,
  );
}

/**
 * For a child whose own entrance waits while a step slides the body in
 * (`.eth-step[data-step-enter] … { animation-delay: -1s }`): mounted during
 * the slide, it marks itself `data-step-arrived`, so its entrance stays skipped
 * once the step settles. Pass `key` when the element is re-keyed inside the same component.
 */
export function useStepArrival(ref: React.RefObject<HTMLElement | null>, key?: unknown): void {
  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (el?.closest("[data-step-enter]")) el.setAttribute("data-step-arrived", "");
  }, [ref, key]);
}

function reducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/** ← → move between siblings when there are steps; otherwise between the sections (the list wraps). */
function arrowSteps(nav: MiniNavProps | null | undefined) {
  return sheetSteps<MiniNavStep>(nav);
}

function Chev({ dir }: { dir: -1 | 1 }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path
        d={dir < 0 ? "M10 3.5 5.5 8 10 12.5" : "M6 3.5 10.5 8 6 12.5"}
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** what the body is showing: a sibling and a section, and how it arrived */
interface View {
  key: string | null;
  section: string | null;
  index: number;
  /** 0 on open; +1 on every step or section change (the body's React key) */
  gen: number;
  dir: -1 | 1;
  swipe: boolean;
}

export default function EtherModal({
  open,
  onClose,
  labelledBy,
  hue = null,
  header,
  footer,
  footStart,
  children,
  size = "md",
  height,
  nav = null,
  onEscape,
  lookAt = null,
  share = null,
  className,
  panelStyle: callerPanelStyle,
}: EtherModalProps) {
  // client-only: the portal needs a body, and the server renders nothing here
  const ready = useSyncExternalStore(noSubscribe, isClient, isServer);

  const navKey = nav?.stepKey ?? null;
  const navSection = nav?.section ?? null;
  const navIndex = nav?.position?.index ?? -1;
  const navTotal = nav?.position?.total ?? 0;
  const sectionIds = nav?.sections?.map((s) => s.id) ?? [];
  const hasSections = sectionIds.length >= 2;
  const hasSteps = !!nav && (!!nav.prev || !!nav.next);
  // a sheet that moves (sections, steps) or that reads (lg and up) is TALL for
  // its whole life; a short one is measured once and held
  const heightMode: "tall" | "fit" = height ?? (hasSections || hasSteps || size === "lg" || size === "xl" ? "tall" : "fit");

  // present = in the DOM; exiting = playing the way out before unmount.
  // Adjusted during render (not in an effect) so the first open frame already
  // has the panel in it.
  const [present, setPresent] = useState(open);
  const [exiting, setExiting] = useState(false);
  // once the entrance has played it is dropped (so a pull can move the sheet;
  // an animation's fill would hold transform at none), and a sheet pulled away
  // leaves on the finger's own motion rather than the exit animation
  const [settled, setSettled] = useState(false);
  const [flung, setFlung] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);
  // a fit sheet's height, measured as it opens and held for its whole life
  const [fitH, setFitH] = useState<number | null>(null);
  const [view, setView] = useState<View>({ key: navKey, section: navSection, index: navIndex, gen: 0, dir: 1, swipe: false });
  // a swipe asked for the step that is about to land: it slides on the glide
  const [swipeArmed, setSwipeArmed] = useState(false);
  // the step whose entrance has finished (children animate their own way again)
  const [settledGen, setSettledGen] = useState(0);
  // the neighbour a step button is pointing at (its name shows beside the steps)
  const [peek, setPeek] = useState<{ dir: -1 | 0 | 1; name: string }>({ dir: 0, name: "" });
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setPresent(true);
      setExiting(false);
      setSettled(false);
      setFlung(false);
      setFitH(null);
      setPeek({ dir: 0, name: "" });
      setView({ key: navKey, section: navSection, index: navIndex, gen: 0, dir: 1, swipe: false });
      setSettledGen(0);
    } else if (present) {
      setExiting(true);
    }
  } else if (open && (navKey !== view.key || navSection !== view.section)) {
    // a step to a sibling (or, the sibling unchanged, to another section):
    // the body arrives from the side it came from
    const d =
      navKey !== view.key
        ? stepDirection(view.index, navIndex, navTotal)
        : stepDirection(sectionIds.indexOf(view.section ?? ""), sectionIds.indexOf(navSection ?? ""), sectionIds.length);
    setView({
      key: navKey,
      section: navSection,
      index: navIndex,
      gen: view.gen + 1,
      dir: d < 0 ? -1 : 1,
      swipe: navKey !== view.key && swipeArmed,
    });
    if (swipeArmed) setSwipeArmed(false);
  }

  useEffect(() => {
    if (!exiting) return;
    const t = window.setTimeout(() => {
      setPresent(false);
      setExiting(false);
    }, EXIT_MS);
    return () => window.clearTimeout(t);
  }, [exiting]);

  // a swipe that did not land a step leaves nothing armed behind it
  useEffect(() => {
    if (!swipeArmed) return;
    const t = window.setTimeout(() => setSwipeArmed(false), 0);
    return () => window.clearTimeout(t);
  }, [swipeArmed]);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const stepRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const onEscapeRef = useRef(onEscape);
  const navRef = useRef(nav);
  useEffect(() => {
    onCloseRef.current = onClose;
    onEscapeRef.current = onEscape;
    navRef.current = nav;
  }, [onClose, onEscape, nav]);

  const shown = ready && present;
  const live = shown && !exiting;
  // read by the key handler: a sheet on its way out never steps
  const liveRef = useRef(live);
  useIsoLayoutEffect(() => {
    liveRef.current = live;
  }, [live]);

  // ── a fit sheet: measured as it opens, then held ────────────────────────────
  useIsoLayoutEffect(() => {
    if (!live || heightMode !== "fit" || fitH != null) return;
    const p = panelRef.current;
    if (!p) return;
    const h = p.offsetHeight;
    if (h > 0) setFitH(h);
  }, [live, heightMode, fitH]);

  // ── focus in, and back out to whoever opened it ─────────────────────────────
  useIsoLayoutEffect(() => {
    if (!shown) return;
    if (!openerRef.current) {
      const a = document.activeElement;
      openerRef.current = a instanceof HTMLElement && a !== document.body ? a : null;
    }
  }, [shown]);
  useEffect(() => {
    if (!live) return;
    // a field that took focus as it mounted (autoFocus, a rename) keeps it
    if (panelRef.current?.contains(document.activeElement)) return;
    const target = closeRef.current ?? focusablesIn(panelRef.current)[0] ?? panelRef.current;
    target?.focus({ preventScroll: true });
  }, [live]);
  // focus goes back to whoever opened the sheet only if it was still ours to
  // give: when another sheet has taken over (a rename opened from the options),
  // that sheet keeps it
  const hadFocusRef = useRef(false);
  useEffect(() => {
    if (!shown) return;
    const note = () => {
      const at = document.activeElement;
      hadFocusRef.current = !!at && (at === document.body || !!rootRef.current?.contains(at));
    };
    document.addEventListener("focusin", note);
    return () => {
      note();
      document.removeEventListener("focusin", note);
    };
  }, [shown]);
  useEffect(() => {
    if (shown) return;
    const opener = openerRef.current;
    openerRef.current = null;
    const at = document.activeElement;
    const ours = hadFocusRef.current || !at || at === document.body;
    if (ours && opener && opener.isConnected) opener.focus({ preventScroll: true });
  }, [shown]);

  // ── Esc steps back a level or closes; focus that wanders outside comes back ─
  useEffect(() => {
    if (!live) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // only the TOP layer answers: a sheet stacked above this one (a rename
      // over the options), or a layer opened over it (the share studio, a
      // menu: [data-gesture-root]) takes its own Esc first
      const sheets = document.querySelectorAll("[data-sheet]");
      if (sheets[sheets.length - 1] !== rootRef.current) return;
      if (document.querySelector("[data-gesture-root]")) return;
      // this is the top layer: nothing underneath (a level up, a menu, the
      // netti corner) should also answer the same press.
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      if (onEscapeRef.current?.()) return;
      onCloseRef.current();
    };
    const onFocusIn = (e: FocusEvent) => {
      const root = rootRef.current;
      if (!root || !(e.target instanceof Node) || root.contains(e.target)) return;
      // a layer opened from inside the sheet (the share studio, a menu) keeps its own focus
      if (e.target instanceof Element && e.target.closest("[data-sheet-layer], [data-gesture-root], [role='menu'], [role='dialog'], [data-sheet]")) return;
      const first = focusablesIn(panelRef.current)[0] ?? panelRef.current;
      first?.focus({ preventScroll: true });
    };
    window.addEventListener("keydown", onKey, true);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [live]);

  // ── the panel's keys: the Tab trap, and ← → (ONE place) ─────────────────────
  const onPanelKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      // the sheet is the top layer for arrows, as it is for Esc: nothing behind
      // it hears them, stepped or not. Stopped, never prevented, so a field
      // keeps its caret.
      e.stopPropagation();
      const steps = arrowSteps(navRef.current);
      const dir = sheetStep(
        {
          key: e.key,
          // a handler deeper in the tree (the navigator's tabs, the chapter rail) already used it
          defaultPrevented: e.isDefaultPrevented() || e.nativeEvent.defaultPrevented,
          metaKey: e.metaKey,
          ctrlKey: e.ctrlKey,
          altKey: e.altKey,
          shiftKey: e.shiftKey,
          isComposing: e.nativeEvent.isComposing,
        },
        e.target instanceof HTMLElement ? e.target : null,
        steps,
        liveRef.current,
      );
      const step = dir < 0 ? steps?.prev : dir > 0 ? steps?.next : null;
      if (!step) return;
      e.preventDefault();
      step.onClick();
      return;
    }
    if (e.key !== "Tab") return;
    // the sheet's stops: the panel's, then the share bar lifted beside it
    const items = focusablesIn(rootRef.current);
    // the real stops either side of what holds focus (the panel itself, or a
    // control out of the Tab order such as a ticker word, can sit between two)
    const at = document.activeElement;
    let before = 0;
    for (const el of items) {
      if (at && el !== at && (at.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING) !== 0) before++;
    }
    const after = items.length - before - (at instanceof HTMLElement && items.includes(at) ? 1 : 0);
    const wrap = tabWrap(items.length, before, after, e.shiftKey);
    if (!wrap) return;
    e.preventDefault();
    if (wrap === "first") items[0].focus();
    else if (wrap === "last") items[items.length - 1].focus();
  }, []);

  // ── the room holds still, and says a sheet is up ────────────────────────────
  useEffect(() => {
    if (!shown) return;
    const unlock = lockScroll("sheet");
    const ungesture = holdGestures();
    const unmark = markSheetOpen();
    return () => {
      unlock();
      ungesture();
      unmark();
    };
  }, [shown]);

  // ── the pull: the grip (any pointer), or the whole head on touch ───────────
  const drag = useRef<{ id: number; y0: number; dy: number; t: number; v: number; el: HTMLElement } | null>(null);

  const startPull = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const panel = panelRef.current;
    if (!panel || drag.current) return;
    drag.current = { id: e.pointerId, y0: e.clientY, dy: 0, t: e.timeStamp, v: 0, el: e.currentTarget };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* best effort */
    }
    panel.style.transition = "none";
    panel.dataset.dragging = "true";
  }, []);

  const onGripPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => startPull(e), [startPull]);

  const onHeadPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // a mouse pulls by the grip only (the head's words stay selectable)
      if (e.pointerType === "mouse") return;
      const t = e.target as HTMLElement | null;
      if (t?.closest?.("button,a,input,textarea,select,[data-nodrag]")) return;
      startPull(e);
    },
    [startPull],
  );

  const onPullMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const d = drag.current;
    const panel = panelRef.current;
    if (!d || !panel || e.pointerId !== d.id) return;
    const raw = e.clientY - d.y0;
    // down follows the finger; up resists (the sheet is already as tall as it gets)
    const dy = raw >= 0 ? raw : -Math.min(14, Math.sqrt(-raw) * 1.4);
    const dt = Math.max(1, e.timeStamp - d.t);
    d.v = (dy - d.dy) / dt;
    d.dy = dy;
    d.t = e.timeStamp;
    panel.style.transform = `translateY(${dy}px)`;
  }, []);

  const endPull = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const d = drag.current;
    const panel = panelRef.current;
    if (!d || !panel || e.pointerId !== d.id) return;
    drag.current = null;
    delete panel.dataset.dragging;
    try {
      d.el.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const reduce = reducedMotion();
    if (e.type !== "pointercancel" && (d.dy > DISMISS_PX || (d.v > FLICK_V && d.dy > 24))) {
      // carry on down from where the finger let go, then close
      setFlung(true);
      panel.style.transition = reduce ? "opacity 0.18s linear" : `transform ${EXIT_MS}ms cubic-bezier(.4,0,1,1), opacity ${EXIT_MS}ms linear`;
      panel.style.transform = reduce ? panel.style.transform : "translateY(102%)";
      if (reduce) panel.style.opacity = "0.001";
      onCloseRef.current();
      // a caller that keeps the sheet open after all must never be left with
      // an invisible sheet still holding the page: it comes back up
      window.setTimeout(() => {
        if (!liveRef.current || !panelRef.current) return;
        const p = panelRef.current;
        p.style.transition = reduce ? "none" : "transform 0.36s var(--ease-spring), opacity 0.2s linear";
        p.style.transform = "";
        p.style.opacity = "";
        setFlung(false);
      }, EXIT_MS + 140);
      return;
    }
    // spring back
    panel.style.transition = reduce ? "none" : "transform 0.36s var(--ease-spring)";
    panel.style.transform = "";
    window.setTimeout(() => {
      if (panelRef.current && !drag.current) panelRef.current.style.transition = "";
    }, 380);
  }, []);

  // ── touch: a sideways swipe on the body steps (siblings, else sections) ────
  // Never on the head (that is the pull), never where something owns its own
  // sideways gesture, and only once the finger has clearly gone sideways: the
  // scroller owns vertical, so a gesture that starts vertical stays a scroll.
  const swipe = useRef<{ id: number; x0: number; y0: number; dx: number; axis: "x" | null; target: HTMLElement; follow: boolean } | null>(null);
  const canSwipe = hasSteps || hasSections;

  const onBodyPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" || !e.isPrimary) return;
    if (!arrowSteps(navRef.current)) return;
    const t = e.target as HTMLElement | null;
    if (!t || t.closest(OWN_SWIPE)) return;
    const off = t.closest("[data-noswipe]");
    if (off && off !== rootRef.current) return;
    swipe.current = { id: e.pointerId, x0: e.clientX, y0: e.clientY, dx: 0, axis: null, target: t, follow: !reducedMotion() };
  }, []);

  const onBodyPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const s = swipe.current;
    if (!s || e.pointerId !== s.id) return;
    const dx = e.clientX - s.x0;
    if (!s.axis) {
      const a = swipeAxis(dx, e.clientY - s.y0);
      if (!a) return;
      if (a === "y" || canScrollX(s.target, dx, panelRef.current)) {
        swipe.current = null;
        return;
      }
      s.axis = "x";
    }
    s.dx = dx;
    const w = stepRef.current;
    if (w && s.follow) {
      // the body leans after the finger, a third of the way (translate, so the
      // entrance animation's transform is never fought)
      w.style.transition = "none";
      w.style.setProperty("translate", `${Math.round(dx * 0.3)}px 0`);
    }
  }, []);

  const endSwipe = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, commit: boolean) => {
      const s = swipe.current;
      if (!s || e.pointerId !== s.id) return;
      swipe.current = null;
      const w = stepRef.current;
      if (w && s.axis) {
        w.style.transition = s.follow ? "translate 0.24s var(--ease-soft)" : "";
        w.style.removeProperty("translate");
      }
      if (!commit || s.axis !== "x") return;
      const dir = swipeStep(s.dx);
      const steps = arrowSteps(navRef.current);
      const step = dir < 0 ? steps?.prev : dir > 0 ? steps?.next : null;
      if (!step) return;
      setSwipeArmed(true);
      step.onClick();
    },
    [setSwipeArmed],
  );

  // ── a step lands: the body starts at its top, and settles ───────────────────
  useIsoLayoutEffect(() => {
    if (view.gen === 0) return;
    const panel = panelRef.current;
    const box = panel?.querySelector<HTMLElement>(".eth-scroll");
    if (box) box.scrollTop = 0;
    // focus that sat in the old body went with it: keep it in the sheet, so
    // the next ← → still lands here
    if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
  }, [view.gen]);
  // the same after any other render: when the focused control is removed focus
  // falls to <body> with no event to catch it, and the next ← → or Tab would
  // miss the sheet. No deps: it checks after every render, and does nothing otherwise.
  useIsoLayoutEffect(() => {
    if (!live) return;
    const panel = panelRef.current;
    const at = document.activeElement;
    if (panel && (!at || at === document.body)) panel.focus({ preventScroll: true });
  });
  useEffect(() => {
    if (view.gen === 0) return;
    const g = view.gen;
    const t = window.setTimeout(() => setSettledGen(g), STEP_SETTLE_MS);
    return () => window.clearTimeout(t);
  }, [view.gen]);

  // a sheet that never heard its animations end (a background tab, a style
  // that dropped them) still lands, so a pull can always move it
  useEffect(() => {
    if (!live || settled) return;
    const t = window.setTimeout(() => setSettled(true), 1000);
    return () => window.clearTimeout(t);
  }, [live, settled]);

  // a fresh open starts from a clean inline style
  useIsoLayoutEffect(() => {
    if (!live) return;
    const p = panelRef.current;
    if (!p) return;
    p.style.transform = "";
    p.style.opacity = "";
    p.style.transition = "";
  }, [live]);

  // the thing this sheet is about, lit in the room behind
  useLookAt(open && !exiting, lookAt);

  if (!shown) return null;

  const tint = safeHue(hue);
  const rootStyle = tint ? ({ ["--eth-hue" as string]: tint } as React.CSSProperties) : undefined;
  const stepped = view.gen > 0;
  // a sheet with no head of its own still says what it is: the crumb's last word
  const lastPlace = nav?.crumb?.length ? nav.crumb[nav.crumb.length - 1].label : null;
  const head =
    header != null ? header : lastPlace ? (
      <h2 id={nav?.titleId} className="eth-title">
        {lastPlace}
      </h2>
    ) : null;
  const panelStyle =
    heightMode === "fit" && fitH
      ? ({ ...callerPanelStyle, ["--eth-fit" as string]: `${fitH}px` } as React.CSSProperties)
      : callerPanelStyle;
  const hasFoot = !!footer || !!footStart || hasSteps || !!share;
  const stepName = (s: MiniNavStep | null | undefined) => (s?.name ?? "").trim();

  return createPortal(
    <div
      ref={rootRef}
      className={`eth-root ph-no-capture${className ? ` ${className}` : ""}`}
      data-sheet=""
      data-organism-frost=""
      data-noswipe=""
      data-state={exiting ? "exit" : "open"}
      data-size={size}
      style={rootStyle}
      onKeyDown={onPanelKeyDown}
    >
      <div className="eth-veil" aria-hidden="true" onClick={() => onCloseRef.current()} />
      <div
        ref={panelRef}
        className="eth-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy ?? nav?.titleId}
        tabIndex={-1}
        data-height={heightMode}
        data-held={heightMode === "fit" && fitH ? "" : undefined}
        data-settled={settled ? "true" : undefined}
        data-flung={flung ? "true" : undefined}
        style={panelStyle}
        onAnimationEnd={(e) => {
          if (exiting) return;
          // landed once the LAST thing to unfold has (the foot, else the body);
          // under reduced motion, once the sheet's own fade has
          const t = e.target as HTMLElement;
          const last = hasFoot ? "eth-foot" : "eth-body";
          if ((e.animationName === "ethUnfold" && t.classList.contains(last)) || (e.animationName === "ethFadeIn" && t === e.currentTarget)) {
            setSettled(true);
          }
        }}
      >
        {/* the grip: pull the sheet down by it with any pointer */}
        <div
          className="eth-grip"
          aria-hidden="true"
          onPointerDown={onGripPointerDown}
          onPointerMove={onPullMove}
          onPointerUp={endPull}
          onPointerCancel={endPull}
        >
          <span />
        </div>
        <div
          className="eth-head"
          data-nav={hasSections ? "" : undefined}
          onPointerDown={onHeadPointerDown}
          onPointerMove={onPullMove}
          onPointerUp={endPull}
          onPointerCancel={endPull}
        >
          {nav?.back ? (
            <button type="button" className="quiet-link eth-back" onClick={nav.back.onClick}>
              <span aria-hidden="true">‹ </span>
              {nav.back.label}
            </button>
          ) : null}
          <button ref={closeRef} type="button" className="icon-circle eth-close" aria-label="close" onClick={() => onCloseRef.current()}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
          {head != null ? <div className="eth-head__slot">{head}</div> : null}
          {hasSections ? (
            <MiniNavSections className="eth-head__nav" sections={nav?.sections} section={nav?.section} onSection={nav?.onSection} />
          ) : null}
        </div>
        <div
          className="eth-body"
          data-steps={canSwipe || undefined}
          onPointerDown={canSwipe ? onBodyPointerDown : undefined}
          onPointerMove={canSwipe ? onBodyPointerMove : undefined}
          onPointerUp={canSwipe ? (e) => endSwipe(e, true) : undefined}
          onPointerCancel={canSwipe ? (e) => endSwipe(e, false) : undefined}
        >
          <div className="eth-scroll" data-sheet-scroll="" data-has-foot={hasFoot || undefined}>
            {nav ? (
              <div
                ref={stepRef}
                key={view.gen}
                className={stepped ? "eth-step section-slide" : "eth-step"}
                data-swipe={stepped && view.swipe ? "" : undefined}
                data-step-enter={stepped && settledGen !== view.gen ? "" : undefined}
                style={{ ["--enter-x" as string]: view.dir < 0 ? "-14px" : "14px" } as React.CSSProperties}
              >
                {children}
              </div>
            ) : (
              children
            )}
          </div>
        </div>
        {hasFoot ? (
          <div className="eth-foot">
            <div className="eth-foot__start">
              {footStart}
              {share ? <SheetShareBar share={share} seat="foot" /> : null}
            </div>
            <div className="eth-foot__end">
              {hasSteps ? (
                <div className="eth-steps" onPointerLeave={() => setPeek((p) => ({ ...p, dir: 0 }))}>
                  <span className="eth-steps__peek" aria-hidden="true" data-on={peek.dir ? "" : undefined}>
                    {peek.name}
                  </span>
                  <button
                    type="button"
                    className="icon-circle eth-stepbtn"
                    disabled={!nav?.prev}
                    onClick={() => nav?.prev?.onClick()}
                    onPointerEnter={() => setPeek({ dir: -1, name: stepName(nav?.prev) })}
                    onFocus={() => setPeek({ dir: -1, name: stepName(nav?.prev) })}
                    onBlur={() => setPeek((p) => ({ ...p, dir: 0 }))}
                  >
                    <Chev dir={-1} />
                    <span className="eth-vh">
                      {nav?.prev ? `${nav.prev.label}${stepName(nav.prev) ? `, ${stepName(nav.prev)}` : ""}` : "nothing before this"}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="icon-circle eth-stepbtn"
                    disabled={!nav?.next}
                    onClick={() => nav?.next?.onClick()}
                    onPointerEnter={() => setPeek({ dir: 1, name: stepName(nav?.next) })}
                    onFocus={() => setPeek({ dir: 1, name: stepName(nav?.next) })}
                    onBlur={() => setPeek((p) => ({ ...p, dir: 0 }))}
                  >
                    <Chev dir={1} />
                    <span className="eth-vh">
                      {nav?.next ? `${nav.next.label}${stepName(nav.next) ? `, ${stepName(nav.next)}` : ""}` : "nothing after this"}
                    </span>
                  </button>
                </div>
              ) : null}
              {footer ? <div className="eth-foot__action">{footer}</div> : null}
            </div>
          </div>
        ) : null}
      </div>
      {/* the share bar, lifted over the veil where the room keeps it (a wide screen) */}
      {share ? <SheetShareBar share={share} seat="room" /> : null}
    </div>,
    document.body,
  );
}
